import { Box, Button, CircularProgress, IconButton, Skeleton, Typography } from '@mui/material';
import { EasTooltip } from 'ui/EasTooltip';
import { useNavigate, useParams } from 'react-router';
import { KeyboardBackspace } from '@mui/icons-material';
import { TMtdReportsItemResponse } from 'redux/reducers/hmrc/types';
import { ETableStructureType, ItemWithId } from 'ui/EasTable/types';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	calculateMtdDataRequest,
	downloadMtdReportPdfRequest,
	downloadMtdReportPdfSuccess,
	getMtdReportRequest,
	updateObligationsRequest,
} from 'redux/reducers/hmrc/reducer';
import { RequestPastReportsDialog } from '../RequestPastReportsDialog';
import {
	getCalculateMtdDataLoad,
	getMtdReport,
	getMtdReportLoad,
	getDownloadMtdReportPdfLoad,
	getUpdateObligationsLoad,
} from 'redux/reducers/hmrc/selectors';
import { TReportDetailsProps } from './types';
import { TypeOfDialog } from '../types';
import { easReportStructure } from '../data/eas-report-structure';
import { EasTableWithoutFilter } from '../../../ui/EasTableWithoutFilter';
import { emReportsDetailStructure } from '../data/em-reports-detail-structure';
import { PATHS } from '../../../routes/constants';
import { getSavedUrl, getSavedUrl2 } from '../../../redux/reducers/auth/selectors';
import { EasSkeleton } from '../../../ui/EasSkeleton';
import { EasTableValue } from '../../../ui/EasTable/EasTableRow/EasTableValue';
import { COLORS } from '../../../services/constants/colors';

export const ReportDetails = <
	TResponse extends TMtdReportsItemResponse<TItem>,
	TItem extends ItemWithId,
>({
	items,
	itemsIsLoading,
	handleAction,
}: TReportDetailsProps<TResponse, TItem>) => {
	const dispatch = useDispatch();
	const params = useParams<{ id?: string }>();
	const navigate = useNavigate();
	const savedUrl = useSelector(getSavedUrl);
	const savedUrl2 = useSelector(getSavedUrl2);
	// const handleReplayHistory = () => {
	// 	window.history.back();
	// };

	const mtdReport = useSelector(getMtdReport);
	const mtdReportIsLoading = useSelector(getMtdReportLoad);
	const calculateMtdDataIsLoading = useSelector(getCalculateMtdDataLoad);
	const downloadMtdReportPdfIsLoading = useSelector(getDownloadMtdReportPdfLoad);
	const updateObligationsIsLoading = useSelector(getUpdateObligationsLoad);
	const [openRequestPastReportsDialog, setOpenRequestPastReportsDialog] = useState(false);

	useEffect(() => {
		if ((items?.data?.length ?? 0) > 0 && params.id) {
			dispatch(getMtdReportRequest({ id: Number(params.id) }));
		}
	}, [items, dispatch, params.id]);

	const handleFileReport = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		handleAction(Number(params.id), TypeOfDialog.FILE_REPORT_DIALOG);
	};

	const handleReplayHistory = () => {
		if (savedUrl) {
			if (savedUrl.includes(PATHS.HMRC_PAGE) && !savedUrl.includes(`${PATHS.HMRC_PAGE}/`)) {
				navigate(savedUrl);
			} else if (
				savedUrl2 &&
				savedUrl2.includes(PATHS.HMRC_PAGE) &&
				!savedUrl2.includes(`${PATHS.HMRC_PAGE}/`)
			) {
				navigate(savedUrl2);
			} else navigate(PATHS.HMRC_PAGE);
		}
	};

	const handleCalculate = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		dispatch(calculateMtdDataRequest({ id: Number(params.id) }));
	};

	const handleDownloadPdf = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		dispatch(downloadMtdReportPdfRequest({ id: Number(params.id) }));
	};

	const handleOpenRequestPastReportsDialog = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		setOpenRequestPastReportsDialog(true);
	};

	const handleRequestPastReportsConfirm = (startDate: string) => {
		dispatch(
			updateObligationsRequest({
				emId: mtdReport?.emId ?? 0,
				startDate,
			}),
		);
	};

	return (
		<Box sx={{ margin: '0px 10px 0 10px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
			{mtdReport && !mtdReportIsLoading && (
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<EasTooltip title="Back to EM Management">
						<IconButton color="primary" sx={{ p: 0 }} onClick={handleReplayHistory}>
							<KeyboardBackspace />
						</IconButton>
					</EasTooltip>
					<Typography
						sx={{
							fontSize: '1.2rem',
							color: COLORS.PRIMARY,
							fontWeight: 'bold',
						}}
					>
						HMRC Reports
					</Typography>
					<EasTableValue
						value={{ id: mtdReport?.emId ?? 0, name: mtdReport?.emFullName ?? '' }}
						type={ETableStructureType.EM}
						size="xl"
						tooltip="EM Full Name"
						color={COLORS.PRIMARY}
					/>
				</Box>
			)}
			{mtdReportIsLoading && <EasSkeleton />}
			<EasTableWithoutFilter
				tableStructure={easReportStructure}
				items={{
					data: mtdReport ? [mtdReport] : [],
					pageNumber: 1,
					pageSize: 10,
					totalCount: mtdReport ? 1 : 0,
				}}
				itemsIsLoading={mtdReportIsLoading}
				handleAction={handleAction}
				skeletonRows={[1]}
				handleLink={() => {}}
			>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', p: 0 }}>
						<Typography
							sx={{
								fontSize: '.9rem',
								color: 'primary.main',
								fontWeight: 'bold',
							}}
						>
							Reports EAS VAT number
						</Typography>
					</Box>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
						<Button
							variant="contained"
							color="primary"
							sx={{ height: '24px', p: '4px 10px' }}
							onClick={handleOpenRequestPastReportsDialog}
							disabled={calculateMtdDataIsLoading || updateObligationsIsLoading}
						>
							{updateObligationsIsLoading && <CircularProgress size={16} />} Request Past Reports
						</Button>
						<Button
							variant="contained"
							color="primary"
							sx={{ height: '24px', p: '4px 10px' }}
							onClick={handleCalculate}
							disabled={calculateMtdDataIsLoading}
						>
							{calculateMtdDataIsLoading && <CircularProgress size={16} />}{' '}
							{calculateMtdDataIsLoading ? 'Calculating...' : 'Calculate'}
						</Button>
						<Button
							variant="contained"
							color="primary"
							sx={{ height: '24px', p: '4px 10px' }}
							onClick={(e) => handleFileReport(e)}
							disabled={mtdReportIsLoading || mtdReport?.reportStatus === 'sent'}
						>
							File Report
						</Button>
						{mtdReport?.reportStatus === 'sent' && (
							<Button
								variant="contained"
								color="primary"
								sx={{ height: '24px', p: '4px 10px' }}
								onClick={handleDownloadPdf}
								disabled={downloadMtdReportPdfIsLoading}
							>
								Download PDF
							</Button>
						)}
					</Box>
				</Box>
			</EasTableWithoutFilter>

			{/* <EasTableWithoutFilter
				tableStructure={tableDetailStructure}
				items={{
					data: mtdReport?.reportedVatDueSales
						? [{ reportedVatDueSales: mtdReport.reportedVatDueSales }]
						: [],
					pageNumber: 1,
					pageSize: 10,
					totalCount: mtdReport?.reportedVatDueSales ?? 0,
				}}
				itemsIsLoading={itemsIsLoading}
				handleAction={handleAction}
			>
				<Typography
					sx={{
						fontSize: '.9rem',
						color: 'primary.main',
						fontWeight: 'bold',
					}}
				>
					Reported VAT Due
				</Typography>
			</EasTableWithoutFilter> */}

			<EasTableWithoutFilter
				tableStructure={emReportsDetailStructure}
				items={{
					data: mtdReport?.reportData ?? [],
					pageNumber: 1,
					pageSize: 10,
					totalCount: mtdReport?.reportData?.length ?? 0,
				}}
				itemsIsLoading={itemsIsLoading}
				handleAction={handleAction}
			>
				<Typography
					sx={{
						fontSize: '.9rem',
						color: 'primary.main',
						fontWeight: 'bold',
					}}
				>
					The VAT amount is calculated by the EAS solution based on the order information
				</Typography>
			</EasTableWithoutFilter>
			<RequestPastReportsDialog
				key={openRequestPastReportsDialog ? (mtdReport?.reportPeriodStart ?? 'open') : 'closed'}
				open={openRequestPastReportsDialog}
				setClose={() => setOpenRequestPastReportsDialog(false)}
				emId={mtdReport?.emId ?? 0}
				defaultStartDate={mtdReport?.reportPeriodStart ?? new Date().toISOString()}
				onConfirm={handleRequestPastReportsConfirm}
			/>
		</Box>
	);
};
